import React, { createContext, useContext, useEffect } from 'react';
import { useDarkMode } from 'usehooks-ts';

interface DarkModeContextType {
  isDarkMode: boolean;
  toggle: () => void;
  enable: () => void;
  disable: () => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(
  undefined
);

export const useDarkModeContext = () => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error(
      'useDarkModeContext must be used within a DarkModeProvider'
    );
  }
  return context;
};

interface DarkModeProviderProps {
  children: React.ReactNode;
}

export const DarkModeProvider: React.FC<DarkModeProviderProps> = ({
  children,
}) => {
  const { isDarkMode, toggle, enable, disable } = useDarkMode({
    localStorageKey: 'itria-dark-mode',
    initializeWithValue: true,
    defaultValue: true,
  });

  // Sync dark mode with document element
  useEffect(() => {
    const root = document.documentElement;
    if (isDarkMode) {
      enable();
      root.classList.add('dark');
    } else {
      disable();
      root.classList.remove('dark');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDarkMode]);

  const value: DarkModeContextType = {
    isDarkMode,
    toggle,
    enable,
    disable,
  };

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  );
};
